import Modal from '../components/Modal';

import {SerializedStyles} from '@emotion/serialize';
import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

type ModalContext = {
  content: ReactNode;
  isLoading: boolean;
  isOpen: boolean;
  onCloseClick: () => void;
  setBodyCss: Dispatch<SetStateAction<SerializedStyles | undefined>>;
  setContent: Dispatch<SetStateAction<ReactNode>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  setOnCloseClick: Dispatch<SetStateAction<() => void>>;
  bodyCss?: SerializedStyles;
} | null;

const ModalContext = createContext<ModalContext>(null);

const initialValues: Omit<
  NonNullable<ModalContext>,
  'setContent' | 'setIsLoading' | 'setIsOpen' | 'setOnCloseClick' | 'setBodyCss'
> = {
  isOpen: false,
  isLoading: false,
  content: null,
  onCloseClick: () => {},
  bodyCss: undefined,
};

/**
 * ModalProvider is a React context provider that manages the global modal state for the application.
 *
 * It provides context values and setters for:
 *   - isOpen: Whether the modal is open
 *   - isLoading: Whether the modal is in a loading state
 *   - content: The ReactNode content to display in the modal
 *   - bodyCss: Emotion styles for the modal body
 *   - onCloseClick: Callback for when the modal is closed
 *   - setIsOpen, setIsLoading, setContent, setBodyCss, setOnCloseClick: Setters for the above
 *
 * The provider also handles browser history popstate events to close the modal when navigating.
 *
 * @example
 * <ModalProvider>
 *   {children}
 * </ModalProvider>
 *
 * @remark All components that need to open, close, or customize the modal should be wrapped in this provider and use the useModal() hook.
 */
export const ModalProvider = ({children}: {children: ReactNode}) => {
  const [isOpen, setIsOpen] = useState(initialValues.isOpen);
  const [isLoading, setIsLoading] = useState(initialValues.isLoading);
  const [content, setContent] = useState(initialValues.content);
  const [bodyCss, setBodyCss] = useState(initialValues.bodyCss);
  const [onCloseClick, setOnCloseClick] = useState(
    () => initialValues.onCloseClick,
  );

  const resetModal = () => {
    setIsOpen(initialValues.isOpen);
    setIsLoading(initialValues.isLoading);
    setContent(initialValues.content);
    setOnCloseClick(() => initialValues.onCloseClick);
    setBodyCss(undefined);
  };

  /**
   * Handles the browser's popstate event, which is triggered when the user navigates
   * through the browser history (e.g., using the back or forward buttons).
   * If the modal is currently open, this function will reset and close the modal.
   *
   * Dependencies:
   * - `isOpen`: A boolean indicating whether the modal is currently open.
   * - `resetModal`: A function to reset and close the modal.
   *
   * This function is memoized using `useCallback` to prevent unnecessary re-creations
   * unless the `isOpen` dependency changes.
   */
  const handlePopState = useCallback(() => {
    if (isOpen) resetModal();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) resetModal();
    else window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [handlePopState, isOpen]);

  const handleOnCloseClick = () => {
    onCloseClick();
    setIsOpen(false);
  };

  return (
    <ModalContext.Provider
      value={{
        isOpen,
        setIsOpen,
        isLoading,
        setIsLoading,
        content,
        setContent,
        setOnCloseClick,
        onCloseClick,
        setBodyCss,
        bodyCss,
      }}
    >
      <Modal
        bodyCss={bodyCss}
        isLoading={isLoading}
        isOpen={isOpen}
        onCloseClick={handleOnCloseClick}
      >
        {content}
      </Modal>
      {children}
    </ModalContext.Provider>
  );
};

/**
 * Custom hook to access the modal context.
 *
 * @returns {ModalContext} The current modal context value.
 * @throws {Error} If used outside of a ModalProvider.
 *
 * @example
 * const { isOpen, setIsOpen } = useModal();
 */
export const useModal = (): NonNullable<ModalContext> => {
  const context = useContext(ModalContext);
  if (!context) throw new Error('useModal must be used within a ModalProvider');
  return context;
};
